import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Layers, Building2 } from 'lucide-react';
import { applicationsService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

function ApplicationsPage() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await applicationsService.getAll();
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingSpinner size="lg" text="Chargement..." />
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-dark-900 via-primary-900 to-dark-900 overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-10"></div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Nos Applications
            </h1>
            <p className="text-xl text-dark-300">
              Solutions digitales innovantes pour les institutions financières
            </p>
          </motion.div>
        </div>
      </section>

      {/* Liste des applications */}
      <section className="section bg-dark-50 py-16">
        <div className="container-custom">
          {applications.length === 0 ? (
            <div className="text-center py-20">
              <Layers className="w-16 h-16 text-dark-300 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-dark-900 mb-2">
                Aucune application disponible
              </h3>
              <p className="text-dark-500">
                Les applications seront affichées ici une fois publiées depuis le panel admin.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {applications.map((app, index) => (
                <motion.article
                  key={app.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="bg-white rounded-2xl overflow-hidden border border-dark-100 hover:shadow-lg transition-shadow h-full">
                    {/* Image */}
                    <div className="aspect-video bg-dark-100 relative overflow-hidden">
                      {app.image_url ? (
                        <img 
                          src={app.image_url} 
                          alt={app.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-dark-200 to-dark-300">
                          <Layers className="w-16 h-16 text-dark-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      {/* Titre */}
                      <h2 className="text-xl font-bold text-dark-900 mb-2">
                        {app.title}
                      </h2>
                      
                      {/* Description */}
                      <p className="text-dark-500 text-sm mb-4">
                        {app.short_description}
                      </p>

                      {/* Footer */}
                      <div className="flex items-center justify-between">
                        {app.client_name && (
                          <div className="flex items-center gap-2 text-sm text-dark-500">
                            <Building2 className="w-4 h-4" />
                            {app.client_name}
                          </div>
                        )}
                        
                        <Link 
                          to={`/nos-applications/${app.slug}`}
                          className="inline-flex items-center text-primary-600 font-medium text-sm hover:text-primary-700 transition-colors"
                        >
                          Voir les détails
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: 'rgb(26, 50, 84)' }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Besoin d'une solution sur mesure ?
            </h2>
            <p className="text-gray-300 mb-8">
              Contactez-nous pour discuter de votre projet et obtenir un devis personnalisé.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-gray-900 transition-colors font-medium"
            >
              Demande de devis
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default ApplicationsPage;